import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot, faArrowRight, faHouse, faBasketShopping } from '@fortawesome/free-solid-svg-icons';
import type { CoffeeRateInterface } from '../types';

interface ShopLinkProps {
    coffeeData: CoffeeRateInterface;
    isAdmin?: boolean;
}

/**
 * The shop affordance in the head. When the rating is tied to a real shop
 * (`coffeeShopRef._id`) it renders as a tappable action pill that routes to
 * `/coffeeshops/:id` — pin + name + arrow, so it reads as "open this shop".
 * Self-made and legacy string-only shops render as a static (non-link) pill.
 * Chain / "N ratings" context now lives in the indicator chips, not here.
 * Admins still see the cluster line below.
 */
export const ShopLink: React.FC<ShopLinkProps> = ({ coffeeData, isAdmin }) => {
    const ref = coffeeData.coffeeShopRef;
    const refId = ref?._id;
    const isSelfMade = !!coffeeData.isSelfMade;
    // supermarket ratings carry a brand instead of a shop — the brand renders
    // in this location slot as a static (non-link) pill; brands have no page.
    const supermarket = coffeeData.supermarket;
    const displayName = supermarket
        ? supermarket.brandName
        : isSelfMade
            ? 'Home Made'
            : (ref?.name || coffeeData.coffeeShop);
    const cluster = isAdmin ? ref?.clusterId : undefined;
    const linkable = !!refId && !isSelfMade && !supermarket;

    return (
        <div className='shop-block'>
            {linkable ? (
                <Link
                    to={`/coffeeshops/${refId}`}
                    className='shop-action'
                    title={`Open ${displayName}`}
                >
                    <FontAwesomeIcon icon={faLocationDot} className='shop-action__pin' aria-hidden />
                    <span className='shop-action__name'>{displayName}</span>
                    <FontAwesomeIcon icon={faArrowRight} className='shop-action__arrow' aria-hidden />
                </Link>
            ) : (
                <div className='shop-action shop-action--static'>
                    <FontAwesomeIcon
                        icon={supermarket ? faBasketShopping : isSelfMade ? faHouse : faLocationDot}
                        className='shop-action__pin'
                        aria-hidden
                    />
                    <span className='shop-action__name'>{displayName}</span>
                </div>
            )}
            {cluster && (
                <div className='cluster-info-admin'>
                    Cluster: {cluster.name}
                    {cluster.city && <> · {cluster.city}, {cluster.country}</>}
                    {cluster.totalShops != null && <> · {cluster.totalShops} shops</>}
                </div>
            )}
        </div>
    );
};
